
import tdocsBridge from '@src/slide/infrastructure/module/independent/tdocsBridge/tdocsBridge';
import ua from '@tencent/docs-user-agent';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { network } from '@tencent/netcheck';
import { ISlideTitleManager, titleChangedCallback } from '../../../core/interfaces/slide-title-manager.interface';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import { ILogger } from '../../../core/slide-data-services.core';
import { createNanoEvents } from 'nanoevents';
import { OnlineEditServices } from '../core/online-edit-services.core';
import { isClientDriveFile } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';

const DEBUG_TAG = '[DefaultSlideTitleManager] ';

interface IUpdateTitleOnlineParams {
    title: string;
    timestamp?: number;
    callback?: (error: any, res: any) => void;
}

// 文档标题发生变更并同步 clientVars 后， 同步本地缓存
export const CACHE_UPDATE_TITLE = 'CACHE_UPDATE_TITLE';

export class DefaultSlideTitleManager implements ISlideTitleManager {
    protected slideTitle: string; // 不带后缀的标题，用于接口传输
    protected offlineTitle?: string; // 离线时暂存的标题，在恢复网络时提交
    protected offlineTitleTimestamp?: number; // 离线时暂存标题的时间戳
    protected isUserChangeTitle = false;
    protected events = createNanoEvents();

    public constructor(
        private clientVarsService: IClientVarsService,
        private logger: ILogger,
    ) {
        const { offlineTitle, offlineTitleTimestamp, isUserChangeTitle } = this.clientVarsService.getClientVars();
        // 兼容离线标题设置为空
        this.offlineTitle = offlineTitle || undefined;
        this.offlineTitleTimestamp = offlineTitleTimestamp;
        this.slideTitle = this.offlineTitle || this.clientVarsService.getClientVars().title || '';
        this.isUserChangeTitle = !!isUserChangeTitle;
        this.registerCommitOfflineTitle();
        window.document.title = this.titleWithSuffix;
        this.setTitleInMobile();
        // 当 title 发生改变时的通用处理
        this.onTitleChanged((title) => {
            this.slideTitle = title;
            window.document.title = this.titleWithSuffix;
            this.isUserChangeTitle = true;
            this.setTitleInMobile();
            this.setDocVisible();
            // 标题发生变更后同步标题至 clientVars 并更新缓存数据
            this.syncTitle2ClientVars(title);
        });
    }

    /**
     * 标题，相对于 titleWithSuffix，此标题不带后缀
     */
    public get title() {
        return this.slideTitle;
    }

    /**
     * 暴露出来的标题，可能带后缀
     */
    public get titleWithSuffix() {
        return this.getFullTitle(this.slideTitle);
    }

    /**
     * 更新 UI 标题
     * 有网则直接更新，无网则先保存offlineTitle 恢复网络后再次提交(主要移动端使用)
     * 有歧义：title === undefined 的时候会使用已有的 title 更新，并不是删除 title
     * @param title 标题文本
     */
    public updateTitle(title?: string, callback?: (error: any, res: any) => void) {
        if (!title || title === this.slideTitle) {
            this.emitTitleChanged(this.slideTitle);
            return;
        }
        if (navigator.onLine) {
            this.updateTitleOnline({ title, callback });
        } else {
            // TODO: 无网状态下可以用 callback 回传状态。目前因为有无网弹窗，所以不执行 callback
            this.updateTitleOffline(title);
        }
    }

    public onTitleChanged(callback: titleChangedCallback) {}

    protected emitTitleChanged(title: string) {}

    protected setDocVisible() {}

    /**
     * 更新标题
     * @param title 待更新的标题
     * @param callback 更新后的额外回调函数
     */
    protected async updateTitleOnline(params: IUpdateTitleOnlineParams) {
        const { padId = '', domainId = '' } = this.clientVarsService.getClientVars() ?? {};
        OnlineEditServices.OnlineApiService.promise().then(({ updateTitle: updateTitleWithCgi }) => {
            const { title, timestamp, callback } = params;
            // 智能修改标题如果传参auto_change=1,不触发isUserChangeTitle改变
            updateTitleWithCgi({ padId, domainId, title, timestamp })
                .then((res) => {
                    // TODO: 12000 为标题不合规错误码，需要移至合适的地方定义错误码
                    if (this.expectRetCode(res.data, 12000)) {
                        callback?.(null, res);
                        return;
                    }
                    callback?.(null, res);
                    // TODO: 待确认，如果 socket 会给自己推送标题变更的话，这里就不需要 emit
                    this.emitTitleChanged(title);
                    this.updateTitleOffline(undefined);
                    tdw({
                        opername: 'doc_non_edit',
                        module: 'title',
                        action: 'modify',
                    });
                })
                .catch((error) => {
                    this.logger.error(DEBUG_TAG, `updateTitle error: ${JSON.stringify(error)}`);
                    callback?.(error, null);
                });
        });
    }

    /**
     * 在 app、小程序里更新原生标题栏
     */
    private setTitleInMobile() {
        // 小程序更新标题
        tdocsBridge.tdocsUi?.setWebViewBehavior({ titleText: `${this.titleWithSuffix}` });
        tdocsBridge.tdocsUi?.refreshTitle();
        // 企微 App 额外调用
        if (ua.isTencentDocsApp && __IS_WECOM__) {
            tdocsBridge.tdocs?.setTitle({ title: this.titleWithSuffix, url: window.location.href });
            return;
        }
        // 以下历史逻辑是 C端 App 额外调用接口更新标题，但是实测没有什么用，还会导致App卡住，如果后续有 bug 可以恢复
        // if (ua.isTencentDocsApp) {
        //     tdocsBridge.tdocsUi?.setWebviewNaviTitle({
        //         title: titleWithSuffix,
        //         url: window.location.href,
        //         refreshDocList: true,
        //     });
        // }
    }

    /**
     * 监听离线编辑标题事件
     * 恢复网络后，如果存在offlineTitle则重新提交修改
     */
    private registerCommitOfflineTitle() {
        const commitOfflineTitle = () => {
            this.logger.info(DEBUG_TAG, `commitOfflineTitle this.offlineTitle: ${this.offlineTitle}`);
            if (this.offlineTitle === undefined) {
                return;
            }
            this.updateTitleOnline({
                title: this.offlineTitle,
                timestamp: this.offlineTitleTimestamp,
            });
        };
        // 初始化时在线同步离线标题
        if (network.isOnline) {
            commitOfflineTitle();
        }
        // 恢复网络后同步离线标题
        window.addEventListener('online', () => {
            commitOfflineTitle();
        });
    }

    /**
     * 获取完整标题用于显示
     */
    private getFullTitle(title: string) {
        if (isClientDriveFile()) {
            const fileExt = (window.basicClientVars?.docInfo as any)?.cosInfo?.driveExt;
            return fileExt ? `${title}.${fileExt}` : title;
        }
        return title;
    }

    /**
     * 比较接口的返回码
     */
    private expectRetCode(data: { retcode?: number; cgicode?: number }, code: number) {
        // success 与关系，fail 或关系
        if (code === 0) return data.retcode === code && data.cgicode === code;
        return data.retcode === code || data.cgicode === code;
    }

    /**
     * 离线更新标题并缓存
     * @param title
     */
    private updateTitleOffline(title: string | undefined) {
        SlideDataServices.ClientVarsService.promise().then((clientVarsData) => {
            const clientVars = clientVarsData.getClientVars();
            /**
             * 只有同步远端创建成功的文档才保留离线标题逻辑
             * 未同步远端的文档在创建的时候会带上最新的标题
             */
            if (!this.clientVarsService?.isLocalDoc()) {
                this.offlineTitle = title;
                this.offlineTitleTimestamp = title === undefined ? undefined : Date.now();
                clientVars.offlineTitle = title;
                clientVars.offlineTitleTimestamp = this.offlineTitleTimestamp;
            }
            // 离线更新标题同步 clientVars 并 emit 事件触发数据库更新
            clientVarsData.setClientVars(clientVars);
            window.dispatchEvent(new CustomEvent(CACHE_UPDATE_TITLE, { detail: {
                // 优先使用离线标题，清除离线标题数据时使用自身 title
                title: title || clientVars.title,
            } }));
            // 离线修改标题时通知标题发生改变
            title && this.emitTitleChanged(title);
        });
    }

    /**
     * 在线同步标题缓存
     * @param title
     */
    private syncTitle2ClientVars(title: string) {
        SlideDataServices.ClientVarsService.promise().then((clientVarsData) => {
            const clientVars = clientVarsData.getClientVars();
            clientVars.title = title;
            clientVars.padTitle = title;
            clientVarsData.setClientVars(clientVars);
            window.dispatchEvent(new CustomEvent(CACHE_UPDATE_TITLE, { detail: { title } }));
        });
    }
}
